import {
  Box,
  Button,
  FormHelperText,
  Radio,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import { useSnackbar } from "notistack";
import AutocompleteSearchField from "../../AutocompleteSearchField";
import ActorAutocompleteOption from "../../ActorAutocompleteOption";
import { actorSearch } from "../../../utils/actorSearch";
import ButtonWithSpinner from "../../ButtonWithSpinner";
import YesOrNoDialog from "../../YesOrNoDialog";
import { useMediaQuery } from "@mui/material";
import DuplicateActorSuggestionsDialog from "./DuplicateActorSuggestionsDialog";
import Preloader from "../../Preloader";
import { Close, InfoOutline } from "@mui/icons-material";
import { actorAddressTypes } from "../../../utils/actorAddressTypes";
import { actorPhoneTypes } from "../../../utils/actorPhoneTypes";

const actorFieldNames = Object.freeze({
  actorId: "actorId",
  postalAddress: "postalAddress",
  invoiceAddress: "invoiceAddress",
  mobilePhone: "mobilePhone",
  businessPhone: "businessPhone",
  actorName: "actorName",
  actorEmail: "actorEmail",
  orgNo: "orgNo",
  birthDate: "birthDate",
  externalActorNo: "externalActorNo",
  webSite: "webSite",
  vatNr: "vatNr",
  vatType: "vatType",
  defaultCustomerOrderNo: "defaultCustomerOrderNo",
  defaultCustomerOrderNoRequired: "defaultCustomerOrderNoRequired",
  observationNote: "observationNote",
  paymentAgreement: "paymentAgreement",
  defaultCurrency: "defaultCurrency",
  languageCode: "languageCode",
});

const actorAddressFieldNames = Object.freeze({
  actorId: "actorId",
  actorAddressId: "actorAddressId",
  actorAddressType: "actorAddressType",
  actorAddressStreet1: "actorAddressStreet1",
  preferredAddresses: "preferredAddresses",
});

const actorPhoneFieldNames = Object.freeze({
  actorId: "actorId",
  actorPhoneId: "actorPhoneId",
  actorPhoneType: "actorPhoneType",
});

function isSameAddress(address1, address2) {
  return (
    address1?.actorAddressType === address2?.actorAddressType &&
    address1?.actorAddressStreet2 === address2?.actorAddressStreet2 &&
    address1?.actorAddressZipCode === address2?.actorAddressZipCode &&
    address1?.actorAddressCity === address2?.actorAddressCity &&
    address1?.actorAddressCountry === address2?.actorAddressCountry
  );
}

// In some cases addresses are added automatically, with nothing but actorAddressStreet1
// set to the actor's name. These will be considered empty here.
function isEmptyAddress(address) {
  if (!address) return true;

  const ignoredProperties = [
    actorAddressFieldNames.actorId,
    actorAddressFieldNames.actorAddressId,
    actorAddressFieldNames.actorAddressType,
    actorAddressFieldNames.actorAddressStreet1,
    actorAddressFieldNames.preferredAddresses,
  ];

  for (const property in address) {
    if (!ignoredProperties.some((ig) => ig === property) && address[property]) {
      return false;
    }
  }
  return true;
}

function isSamePhone(phone1, phone2) {
  return phone1?.actorPhoneType === phone2?.actorPhoneType && phone1?.actorPhoneNumber === phone2?.actorPhoneNumber;
}

// In some cases empty phone numbers are added automatically, these will be considered empty here.
function isEmptyPhone(phone) {
  if (!phone) return true;

  const ignoredProperties = [
    actorPhoneFieldNames.actorId,
    actorPhoneFieldNames.actorPhoneId,
    actorPhoneFieldNames.actorPhoneType,
  ];

  for (const property in phone) {
    if (!ignoredProperties.some((ig) => ig === property) && phone[property]) {
      return false;
    }
  }
  return true;
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export default function DuplicateActorMerger({
  actorId1,
  actorId2,
  descriptionText = "",
  showKeepBothButton = false,
  onClose,
  disableShowNavigateQuestion,
}) {
  const [actor1, setActor1] = useState({});
  const [actor2, setActor2] = useState({});
  const [selectedValueActorIds, setSelectedValueActorIds] = useState({});
  const [selectedAddressesByType, setSelectedAddressesByType] = useState({});
  const [selectedPhonesByType, setSelectedPhonesByType] = useState({});
  const [errors, setErrors] = useState({});
  const [isValidatingValue, setIsValidatingValue] = useState(false);
  const [hasValidatedActors, setHasValidatedActors] = useState(false);
  const [showNavigateQuestion, setShowNavigateQuestion] = useState(false);
  const [showSuggestionsDialog, setShowSuggestionsDialog] = useState(false);
  const [duplicateSuggestionsCount, setDuplicateSuggestionsCount] = useState(null);
  const [autoMatchDetails, setAutoMatchDetails] = useState({});
  const [isSuggestion, setIsSuggestion] = useState(!!actorId1 && !!actorId2);
  const api = useApi();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const location = useLocation();
  const navigate = useNavigate();
  const closeAndReload = () => {
    //Soft reload with redirect
    const loc = encodeURIComponent(location.pathname);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      closeAndReload();
    }
  };

  const smallScreen = useMediaQuery("(max-width:700px)");

  function handleNavigateToResult() {
    if (actor1.actorTypeIds?.some((a) => a === 1)) {
      navigate(`/dashboard/company/${actor1.actorId}`, { replace: false });
    } else {
      navigate(`/dashboard/person/${actor1.actorId}`, { replace: false });
    }
  }

  const loadActor = useCallback(async (actorId) => {
    const result = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}actors/${actorId}/for-duplicate-merge`,
      false,
      "GET",
    );
    return result;
  }, []);

  useEffect(() => {
    if (actorId1) {
      loadActor(actorId1).then((result) => setActor1(result));
    }
  }, [actorId1, loadActor]);

  useEffect(() => {
    if (actorId2) {
      loadActor(actorId2).then((result) => setActor2(result));
    }
  }, [actorId2, loadActor]);

  useEffect(() => {
    if (actor1.actorId && actor2.actorId) {
      const req = {
        actorId1: actor1.actorId,
        actorId2: actor2.actorId,
      };
      api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/merge/validate`, req).then((response) => {
        const validationOk = response && response.isSuccessful && response.validationOk;
        setErrors((oldErrors) => ({
          ...oldErrors,
          actors: validationOk
            ? null
            : response.errorMessageTranslationKey
              ? Translate.get(response.errorMessageTranslationKey)
              : Translate.get("ActorMergeNotPossibleSelectedActors"),
        }));
        setHasValidatedActors(true);
      });
    } else {
      setErrors((oldErrors) => ({ ...oldErrors, actors: null }));
      setHasValidatedActors(false);
    }
  }, [actor1, actor2]);

  useEffect(() => {
    if (!isSuggestion || !actor1.actorId || !actor2.actorId || autoMatchDetails.hide) {
      // console.log("MATCH DETAILS: not shown", !isSuggestion, !actor1.actorId, !actor2.actorId, autoMatchDetails.hide);
      return;
    }

    const duplicate =
      actor1.duplicates.find((d) => d.actorIdOfDuplicate === actor2.actorId) ??
      actor2.duplicates.find((d) => d.actorIdOfDuplicate === actor1.actorId);
    if (!duplicate) {
      // console.log("MATCH DETAILS: no duplicate found");
      return;
    }

    let matchedFields = [];
    if (duplicate.isMatchingEmail && actor1.actorEmail) {
      matchedFields.push(actor1.actorEmail);
    }
    if (duplicate.isMatchingOrgNo && actor1.orgNo) {
      matchedFields.push(actor1.orgNo);
    }
    if (duplicate.isMatchingExternalActorNo && actor1.externalActorNo) {
      matchedFields.push(actor1.externalActorNo);
    }
    if (duplicate.isMatchingNameAndCompany && actor1.actorName && duplicate.matchingCompanyActorName) {
      matchedFields.push(
        `${actor1.actorName} ${Translate.get("And").toLowerCase()} ${
          duplicate.matchingCompanyActorName
        } (${Translate.get("DuplicateFieldNameAndCompany").toLowerCase()})`,
      );
    }

    if (matchedFields.length === 0) {
      // If we arrive here then the actors may have changed after being marked as duplicates.
      // Perhaps we could show a message explaining this? Or perhaps better to not mention it?
      // Going with the latter approach for now...
      // console.log("MATCH DETAILS: no matched fields");
      return;
    }

    setAutoMatchDetails({
      matchExplanation: `${Translate.get("DuplicateMatchingFieldsExplanation")}: ${matchedFields.join(", ")}`,
    });
  }, [actorId1, actorId2, actor1, actor2]);

  useEffect(() => {
    async function countDuplicateSuggestions() {
      const count = await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/duplicates/count`, false, "GET");
      setDuplicateSuggestionsCount(count);
    }
    countDuplicateSuggestions();
  }, [api]);

  function validateSelectedValue(propertyName, actorId) {
    setErrors({ ...errors, [propertyName]: null });
    setIsValidatingValue(true);

    const req = {
      actorId1: actor1.actorId,
      actorId2: actor2.actorId,
      selectedValueActorIds: { [capitalizeFirstLetter(propertyName)]: actorId },
      selectedPhonesByType,
    };
    api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/merge/validate-values`, req).then((response) => {
      const validationOk = response && response.isSuccessful && response.validationOk;
      setErrors({
        ...errors,
        [propertyName]: validationOk
          ? null
          : (response.errorMessageTranslationKey ?? Translate.get("ActorMergeNotPossibleSelectedValue")),
      });
      setIsValidatingValue(false);
    });
  }

  async function mergeActors() {
    if (!actor1.actorId || !actor2.actorId) throw new Error();

    const req = {
      actorId1: actor1.actorId,
      actorId2: actor2.actorId,
      selectedValueActorIds: Object.fromEntries(
        Object.entries(selectedValueActorIds).map((kv) => [capitalizeFirstLetter(kv[0]), kv[1]]),
      ),
      selectedAddressesByType,
      selectedPhonesByType,
    };
    const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/merge`, req);
    if (response && response.isSuccessful) {
      if (disableShowNavigateQuestion) {
        handleClose();
      } else {
        setShowNavigateQuestion(true);
      }
    }
  }

  const getActorTypeToSearch = useCallback(() => {
    if (actor1.actorId) {
      return actor1.actorTypeIds?.some((a) => a === 1) ? [1] : [0, 3, 4, 6, 9];
    } else if (actor2.actorId) {
      return actor2.actorTypeIds?.some((a) => a === 1) ? [1] : [0, 3, 4, 6, 9];
    } else {
      return [0, 1, 3, 4, 6, 9];
    }
  }, [actor1, actor2]);

  const actorNameSearch = useCallback(
    async (searchText) => {
      const foundActors = await actorSearch(
        searchText,
        getActorTypeToSearch(),
        ["ActorName", "OrgNo", "ActorEmail"],
        50,
      );
      if (foundActors) {
        return foundActors.map((actor) => ({
          actorId: actor.actorId,
          actorName: actor.actorName,
          orgNo: actor.orgNo,
          email: actor.email,
          companies: actor.parentActors
            ?.filter(
              (x, index, allParentActors) =>
                x.parentActor.actorId > 0 &&
                x.parentActor.actorId !== actor.actorId &&
                allParentActors.findIndex((y) => y.parentActor.actorId === x.parentActor.actorId) === index,
            )
            .map((x) => x.parentActor.actorName),
        }));
      }
    },
    [getActorTypeToSearch],
  );

  const getPreselectedAddressId = (addressType, thisActor, other) =>
    !isEmptyAddress(thisActor[addressType]) && isEmptyAddress(other[addressType])
      ? thisActor[addressType].actorAddressId
      : isEmptyAddress(thisActor[addressType]) && !isEmptyAddress(other[addressType])
        ? other[addressType].actorAddressId
        : undefined;

  const getPreselectedPhoneId = (phoneType, thisActor, other) =>
    !isEmptyPhone(thisActor[phoneType]) && isEmptyPhone(other[phoneType])
      ? thisActor[phoneType].actorPhoneId
      : isEmptyPhone(thisActor[phoneType]) && !isEmptyPhone(other[phoneType])
        ? other[phoneType].actorPhoneId
        : undefined;

  async function handleActorChange(actor, setActor) {
    setIsSuggestion(false);
    setAutoMatchDetails({});

    if (actor && actor.actorId > 0) {
      // Set the incomplete actor data from search to update
      // UI already before loading of complete actor finishes
      setActor(actor);
      var loadedActor = await loadActor(actor.actorId);
      setActor(loadedActor);

      const other = otherActor(loadedActor);
      setSelectedAddressesByType({
        [actorAddressTypes.postal]: getPreselectedAddressId(actorFieldNames.postalAddress, loadedActor, other),
        [actorAddressTypes.billing]: getPreselectedAddressId(actorFieldNames.invoiceAddress, loadedActor, other),
      });
      setSelectedPhonesByType({
        [actorPhoneTypes.mobile]: getPreselectedPhoneId(actorFieldNames.mobilePhone, loadedActor, other),
        [actorPhoneTypes.business]: getPreselectedPhoneId(actorFieldNames.businessPhone, loadedActor, other),
      });
    } else {
      setActor({
        actorName: actor?.actorName,
      });
      setSelectedValueActorIds({});
      setSelectedAddressesByType({});
      setSelectedPhonesByType({});
    }
    // for(let i = 0; i < actor.)
  }

  async function handleSelectDuplicateSuggestion(duplicate) {
    await handleActorChange(duplicate.actor1, setActor1);
    await handleActorChange(duplicate.actor2, setActor2);
    setIsSuggestion(true);
  }

  function selectValue(propertyName, actorId) {
    const selectedValuesCopy = {
      ...selectedValueActorIds,
      [propertyName]: actorId,
    };
    setSelectedValueActorIds(selectedValuesCopy);
    validateSelectedValue(propertyName, actorId);
  }

  function selectAddress(address) {
    const selectedAddressesCopy = {
      ...selectedAddressesByType,
      [address.actorAddressType]: address.actorAddressId,
    };
    setSelectedAddressesByType(selectedAddressesCopy);
  }

  function selectPhone(phone) {
    const selectedPhonesCopy = {
      ...selectedPhonesByType,
      [phone.actorPhoneType]: phone.actorPhoneId,
    };
    setSelectedPhonesByType(selectedPhonesCopy);
  }

  const Address = useCallback(
    ({ address }) => (
      <>
        <Box>{address?.actorAddressStreet2}</Box>
        <Box>
          {address?.actorAddressZipCode}
          {address?.actorAddressZipCode && address?.actorAddressCity && ","} {address?.actorAddressCity}
        </Box>
      </>
    ),
    [],
  );

  const otherActor = (actor) => (actor.actorId === actor1?.actorId ? actor2 : actor1);
  const isEmptyField = (actor, propertyName) => (actor[propertyName] ? false : true);
  const isFieldSelected = (actor, propertyName) =>
    selectedValueActorIds[propertyName] === actor.actorId ||
    (!isEmptyField(actor, propertyName) && isEmptyField(otherActor(actor), propertyName));

  const isAddressSelected = (actor, propertyName) =>
    !isEmptyAddress(actor[propertyName]) &&
    (selectedAddressesByType[actor[propertyName].actorAddressType] === actor[propertyName].actorAddressId ||
      isEmptyAddress(otherActor(actor)[propertyName]));

  const isPhoneSelected = (actor, propertyName) => {
    return (
      !isEmptyPhone(actor[propertyName]) &&
      (selectedPhonesByType[actor[propertyName].actorPhoneType] === actor[propertyName].actorPhoneId ||
        isEmptyPhone(otherActor(actor)[propertyName]))
    );
  };
  function createField(displayName, propertyName) {
    return {
      displayName,
      propertyName,
      isVisible:
        (!isEmptyField(actor1, propertyName) || !isEmptyField(actor2, propertyName)) &&
        actor1[propertyName] !== actor2[propertyName],
      render: (actor) => actor[propertyName],
      isDisabled: (actor) => isEmptyField(actor, propertyName),
      isSelected: (actor) => isFieldSelected(actor, propertyName),
      select: (actor) => selectValue(propertyName, actor.actorId),
    };
  }

  function createAddressField(propertyName) {
    return {
      displayName: Translate.getActorAddressType(actor1[propertyName]?.actorAddressType),
      propertyName,
      isVisible:
        (!isEmptyAddress(actor1[propertyName]) || !isEmptyAddress(actor2[propertyName])) &&
        !isSameAddress(actor1[propertyName], actor2[propertyName]),
      render: (actor) => (!isEmptyAddress(actor[propertyName]) ? <Address address={actor[propertyName]} /> : ""),
      isDisabled: (actor) => isEmptyAddress(actor[propertyName]),
      isSelected: (actor) => isAddressSelected(actor, propertyName),
      select: (actor) => selectAddress(actor[propertyName]),
    };
  }

  function createPhoneField(propertyName) {
    return {
      displayName: Translate.getActorPhoneType(actor1[propertyName]?.actorPhoneType),
      propertyName,
      isVisible:
        (!isEmptyPhone(actor1[propertyName]) || !isEmptyPhone(actor2[propertyName])) &&
        !isSamePhone(actor1[propertyName], actor2[propertyName]),
      render: (actor) => actor[propertyName]?.actorPhoneNumber,
      isDisabled: (actor) => isEmptyPhone(actor[propertyName]),
      isSelected: (actor) => isPhoneSelected(actor, propertyName),
      select: (actor) => selectPhone(actor[propertyName]),
    };
  }

  const fields =
    actor1.actorId && actor2.actorId && !errors.actors
      ? [
          createField(Translate.get("Name"), actorFieldNames.actorName),
          createField(Translate.get("Email"), actorFieldNames.actorEmail),
          createField(
            getActorTypeToSearch()?.some((t) => t === 1) ? Translate.get("OrgNo") : Translate.get("PersonalNo"),
            actorFieldNames.orgNo,
          ),
          createField(Translate.get("BirthDate"), actorFieldNames.birthDate),
          createField(Translate.get("ExternalActorNo"), actorFieldNames.externalActorNo),
          createField(Translate.get("Website"), actorFieldNames.webSite),
          createField(Translate.get("VatNo"), actorFieldNames.vatNr),
          createField(Translate.get("VatType"), actorFieldNames.vatType),
          createField(Translate.get("DefaultCustomerOrderNo"), actorFieldNames.defaultCustomerOrderNo),
          createField(Translate.get("DefaultCustomerOrderNoRequired"), actorFieldNames.defaultCustomerOrderNoRequired),
          createField(Translate.get("ObservationNote"), actorFieldNames.observationNote),
          createField(Translate.get("PaymentTerm"), actorFieldNames.paymentAgreement),
          createField(Translate.get("CurrencyLabel"), actorFieldNames.defaultCurrency),
          createField(Translate.get("Language"), actorFieldNames.languageCode),

          createAddressField(actorFieldNames.postalAddress),
          createAddressField(actorFieldNames.invoiceAddress),
          createPhoneField(actorFieldNames.mobilePhone),
          createPhoneField(actorFieldNames.businessPhone),
        ]
      : [];

  function hideOptionsFilter(option) {
    return (
      (actor1.actorId > 0 && option.actorId === actor1.actorId) ||
      (actor2.actorId > 0 && option.actorId === actor2.actorId)
    );
  }

  function showField(field, actor) {
    const output = field.render(actor);
    return output ? output : <Box sx={{ color: "text.disabled" }}>{Translate.get("EmptyFieldInDuplicateMerge")}</Box>;
  }

  async function keepBothActors() {
    let response = null;
    let reqObj = { actorId1: actorId2, actorId2: actorId1 };
    try {
      response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}actors/duplicate/delete`,
        reqObj,
        "POST",
        false,
        true,
      );
      if (response.isSuccessful) {
        handleClose();
      }
    } catch (error) {
      const errorMessage = enqueueSnackbar(Translate.get(response.errorMessageTranslationKey || "SomethingFailed"), {
        variant: "error",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(errorMessage),
      });
    }
    return response;
  }

  if (showSuggestionsDialog) {
    return (
      <DuplicateActorSuggestionsDialog
        handleSelect={handleSelectDuplicateSuggestion}
        open={true}
        closeDialog={() => setShowSuggestionsDialog(false)}
      />
    );
  }

  // Datorvy
  if (!smallScreen) {
    return (
      <>
        {isSuggestion && !(actor1.actorId && actor2.actorId && hasValidatedActors) && <Preloader />}
        <DialogContent>
          <Typography sx={{ margin: "0 0 24px" }}>
            {descriptionText === "" ? Translate.get("ActorMergeInfo") : Translate.get(descriptionText)}
          </Typography>
          <DuplicateAutoMatchExplanationBox
            autoMatchDetails={autoMatchDetails}
            onClose={() => setAutoMatchDetails((prev) => ({ ...prev, hide: true }))}
          />
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="300px">{/* Empty cell on purpose */}</TableCell>
                  <TableCell>
                    <AutocompleteSearchField
                      disabled={!!actorId1}
                      placeholder={`${Translate.get("Name")} 1`}
                      label={`${Translate.get("Name")} 1`}
                      value={actor1}
                      onValueChange={(actor) => handleActorChange(actor, setActor1)}
                      getOptionLabel={(option) => option?.actorName ?? ""}
                      renderOption={(option) => (
                        <ActorAutocompleteOption
                          actorName={option?.actorName}
                          orgNo={option?.orgNo}
                          email={option?.email}
                          companies={option?.companies}
                        />
                      )}
                      keyPropName="actorId"
                      requireSelection={true}
                      disableAddAsNew={true}
                      search={actorNameSearch}
                      createNewValue={(text) => ({
                        actorName: text,
                      })}
                      hideOptionsFilter={hideOptionsFilter}
                      minWidth="300px"
                      textFieldProps={{
                        error: errors.actors,
                        helperText: errors.actors,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <AutocompleteSearchField
                      disabled={!!actorId2}
                      placeholder={`${Translate.get("Name")} 2`}
                      label={`${Translate.get("Name")} 2`}
                      value={actor2}
                      onValueChange={(actor) => handleActorChange(actor, setActor2)}
                      getOptionLabel={(option) => option?.actorName ?? ""}
                      renderOption={(option) => (
                        <ActorAutocompleteOption
                          actorName={option?.actorName}
                          orgNo={option?.orgNo}
                          email={option?.email}
                          companies={option?.companies}
                        />
                      )}
                      keyPropName="actorId"
                      requireSelection={true}
                      disableAddAsNew={true}
                      search={actorNameSearch}
                      createNewValue={(text) => ({
                        actorName: text,
                      })}
                      hideOptionsFilter={hideOptionsFilter}
                      minWidth="300px"
                      textFieldProps={{
                        error: errors.actors,
                        helperText: errors.actors && " ",
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hasValidatedActors &&
                  fields.map(
                    (field, index) =>
                      field.isVisible && (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row" align="left">
                            <Box fontWeight="bold">{field.displayName}</Box>
                          </TableCell>
                          <TableCell align="left">
                            <Grid container direction="column">
                              <Grid container justifyContent="flex-start" alignItems="center">
                                <Grid>
                                  <Radio
                                    color={errors[field.propertyName] ? "error" : "default"}
                                    disabled={field.isDisabled(actor1)}
                                    checked={field.isSelected(actor1)}
                                    onChange={() => field.select(actor1)}
                                    name={`radio-buttons-${field.propertyName}`}
                                  />
                                </Grid>
                                <Grid>{showField(field, actor1)}</Grid>
                              </Grid>
                              {errors[field.propertyName] && (
                                <Grid>
                                  <FormHelperText error sx={{ marginLeft: "12px" }}>
                                    {field.isSelected(actor1) ? errors[field.propertyName] : " "}
                                  </FormHelperText>
                                </Grid>
                              )}
                            </Grid>
                          </TableCell>
                          <TableCell align="left">
                            <Grid container direction="column">
                              <Grid container justifyContent="flex-start" alignItems="center">
                                <Grid>
                                  <Radio
                                    color={errors[field.propertyName] ? "error" : "default"}
                                    disabled={field.isDisabled(actor2)}
                                    checked={field.isSelected(actor2)}
                                    onChange={() => field.select(actor2)}
                                    name={`radio-buttons-${field.propertyName}`}
                                  />
                                </Grid>
                                <Grid>{showField(field, actor2)}</Grid>
                              </Grid>
                              {errors[field.propertyName] && (
                                <Grid>
                                  <FormHelperText error sx={{ marginLeft: "12px" }}>
                                    {field.isSelected(actor2) ? errors[field.propertyName] : " "}
                                  </FormHelperText>
                                </Grid>
                              )}
                            </Grid>
                          </TableCell>
                        </TableRow>
                      ),
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowSuggestionsDialog(true)}
            disabled={!!actor1?.actorId || !!actor2?.actorId || !duplicateSuggestionsCount}
          >
            {`${Translate.get("ShowSuggestions")} (${duplicateSuggestionsCount ?? "-"})`}
          </Button>
          <Button onClick={handleClose}>{Translate.get("Cancel")}</Button>
          {showKeepBothButton === true && (
            <ButtonWithSpinner onClick={keepBothActors}>{Translate.get("KeepBoth")}</ButtonWithSpinner>
          )}
          <ButtonWithSpinner
            onClick={mergeActors}
            disabled={
              !hasValidatedActors ||
              isValidatingValue ||
              Object.values(errors).some((e) => e) ||
              !actor1.actorId ||
              !actor2.actorId ||
              fields.filter((f) => f.isVisible).some((f) => !f.isSelected(actor1) && !f.isSelected(actor2))
            }
          >
            {Translate.get("MergeDuplicates")}
          </ButtonWithSpinner>
        </DialogActions>
        <YesOrNoDialog
          open={showNavigateQuestion}
          title={Translate.get("MergeDuplicatesDone")}
          text={Translate.get("MergeDuplicatesDoneInfo")}
          onNo={() => handleClose()}
          onYes={() => handleNavigateToResult()}
          noText={Translate.get("No")}
          yesText={Translate.get("Yes")}
        />
      </>
    );
  }

  // Mobilvy
  return (
    <>
      {isSuggestion && !(actor1.actorId && actor2.actorId && hasValidatedActors) && <Preloader />}
      <DialogContent>
        <Typography sx={{ margin: "0 0 24px" }}>
          {descriptionText === "" ? Translate.get("ActorMergeInfo") : Translate.get(descriptionText)}
        </Typography>
        <DuplicateAutoMatchExplanationBox
          autoMatchDetails={autoMatchDetails}
          onClose={() => setAutoMatchDetails((prev) => ({ ...prev, hide: true }))}
          smallScreen
        />
        <AutocompleteSearchField
          disabled={!!actorId1}
          placeholder={`${Translate.get("Name")} 1`}
          label={`${Translate.get("Name")} 1`}
          value={actor1}
          onValueChange={(actor) => handleActorChange(actor, setActor1)}
          getOptionLabel={(option) => option?.actorName ?? ""}
          renderOption={(option) => (
            <ActorAutocompleteOption
              actorName={option?.actorName}
              orgNo={option?.orgNo}
              email={option?.email}
              companies={option?.companies}
            />
          )}
          keyPropName="actorId"
          requireSelection={true}
          disableAddAsNew={true}
          search={actorNameSearch}
          createNewValue={(text) => ({
            actorName: text,
          })}
          hideOptionsFilter={hideOptionsFilter}
          minWidth="300px"
          textFieldProps={{
            error: errors.actors,
            helperText: errors.actors,
          }}
        />

        <br />

        <AutocompleteSearchField
          disabled={!!actorId2}
          placeholder={`${Translate.get("Name")} 2`}
          label={`${Translate.get("Name")} 2`}
          value={actor2}
          onValueChange={(actor) => handleActorChange(actor, setActor2)}
          getOptionLabel={(option) => option?.actorName ?? ""}
          renderOption={(option) => (
            <ActorAutocompleteOption
              actorName={option?.actorName}
              orgNo={option?.orgNo}
              email={option?.email}
              companies={option?.companies}
            />
          )}
          keyPropName="actorId"
          requireSelection={true}
          disableAddAsNew={true}
          search={actorNameSearch}
          createNewValue={(text) => ({
            actorName: text,
          })}
          hideOptionsFilter={hideOptionsFilter}
          minWidth="300px"
          textFieldProps={{
            error: errors.actors,
            helperText: errors.actors && " ",
          }}
        />

        <br />

        {hasValidatedActors &&
          fields.map(
            (field, index) =>
              field.isVisible && (
                <div key={index}>
                  <Box fontWeight="bold">{field.displayName}</Box>
                  <Grid container direction="column">
                    <Grid container justifyContent="flex-start" alignItems="center">
                      <Grid>
                        <Radio
                          color={errors[field.propertyName] ? "error" : "default"}
                          checked={field.isSelected(actor1)}
                          onChange={() => field.select(actor1)}
                          name={`radio-buttons-${field.propertyName}`}
                        />
                      </Grid>
                      <Grid>{showField(field, actor1)}</Grid>
                    </Grid>
                    {errors[field.propertyName] && (
                      <Grid>
                        <FormHelperText error sx={{ marginLeft: "12px" }}>
                          {field.isSelected(actor1) ? errors[field.propertyName] : " "}
                        </FormHelperText>
                      </Grid>
                    )}
                  </Grid>
                  <Grid container direction="column">
                    <Grid container justifyContent="flex-start" alignItems="center">
                      <Grid>
                        <Radio
                          color={errors[field.propertyName] ? "error" : "default"}
                          checked={field.isSelected(actor2)}
                          onChange={() => field.select(actor2)}
                          name={`radio-buttons-${field.propertyName}`}
                        />
                      </Grid>
                      <Grid>{showField(field, actor2)}</Grid>
                    </Grid>
                    {errors[field.propertyName] && (
                      <Grid>
                        <FormHelperText error sx={{ marginLeft: "12px" }}>
                          {field.isSelected(actor2) ? errors[field.propertyName] : " "}
                        </FormHelperText>
                      </Grid>
                    )}
                  </Grid>
                </div>
              ),
          )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setShowSuggestionsDialog(true)}
          disabled={!!actor1?.actorId || !!actor2?.actorId || !duplicateSuggestionsCount}
        >
          {`${Translate.get("ShowSuggestions")} (${duplicateSuggestionsCount ?? "-"})`}
        </Button>
        <Button onClick={handleClose}>{Translate.get("Cancel")}</Button>
        {showKeepBothButton === true && (
          <ButtonWithSpinner onClick={keepBothActors}>{Translate.get("KeepBoth")}</ButtonWithSpinner>
        )}
        <ButtonWithSpinner
          onClick={mergeActors}
          disabled={
            !hasValidatedActors ||
            isValidatingValue ||
            Object.values(errors).some((e) => e) ||
            !actor1.actorId ||
            !actor2.actorId ||
            fields.filter((f) => f.isVisible).some((f) => !f.isSelected(actor1) && !f.isSelected(actor2))
          }
        >
          {Translate.get("MergeDuplicates")}
        </ButtonWithSpinner>
      </DialogActions>
      <YesOrNoDialog
        open={showNavigateQuestion}
        title={Translate.get("MergeDuplicatesDone")}
        text={Translate.get("MergeDuplicatesDoneInfo")}
        onNo={() => handleClose()}
        onYes={() => handleNavigateToResult()}
        noText={Translate.get("No")}
        yesText={Translate.get("Yes")}
      />
    </>
  );
}

function DuplicateAutoMatchExplanationBox({ autoMatchDetails, onClose, smallScreen }) {
  if (!autoMatchDetails || !autoMatchDetails.matchExplanation || autoMatchDetails.hide) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      padding={1}
      paddingLeft={2}
      marginBottom={smallScreen ? 4 : 2}
      className="textbox blue"
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <InfoOutline />
        <Box>{autoMatchDetails.matchExplanation}</Box>
      </Stack>
      <IconButton onClick={onClose}>
        <Close />
      </IconButton>
    </Stack>
  );
}
